import { FC, useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router';

import { LOTTERY_COPY } from 'src/constants/lottery/copy';
import { Z_INDEX } from 'src/constants/zIndex';
import { useLotteryTraySocket } from 'src/hooks/Lottery/useLotteryTraySocket';
import TrayBatchRow from './TrayBatchRow';
import { computeTrayMetric, formatTrayMetric } from './trayMetric';

export const TRAY_EXPANDED_LS_KEY = 'lottery_tray_expanded';

// The support chat FAB (FloatingChatButton) is `position: fixed; bottom: 24; right: 24`
// at 48px (Fab size="medium"), and it sits ABOVE the tray in the z-index stack. The tray
// shares the bottom-right corner, so it reserves the FAB's footprint plus a 24px gap:
// 24 (gap) + 48 (FAB) + 24 (base inset) = 96px of right inset.
const CHAT_FAB_CLEARANCE_PX = 96;
const BASE_INSET_PX = 24;

function readExpanded(): boolean {
  return window.localStorage.getItem(TRAY_EXPANDED_LS_KEY) === 'true';
}

export interface FloatingBatchTrayProps {
  enabled?: boolean;
}

const FloatingBatchTray: FC<FloatingBatchTrayProps> = ({ enabled = true }) => {
  const { batches, isConnected } = useLotteryTraySocket(enabled);
  const [expanded, setExpanded] = useState(readExpanded);

  useEffect(() => {
    window.localStorage.setItem(TRAY_EXPANDED_LS_KEY, String(expanded));
  }, [expanded]);

  const handleToggle = useCallback((): void => {
    setExpanded((value) => !value);
  }, []);

  // No non-terminal batches: render nothing, not a collapsed nub.
  if (batches.length === 0) {
    return null;
  }

  const metric = computeTrayMetric(batches);
  const metricText = formatTrayMetric(metric);
  const top = batches[0];
  const viewAllPath = `/workbench/${top.property.slug}/lottery`;

  return (
    <Box
      data-testid="tray-root"
      sx={{
        position: 'fixed',
        inset: 0,
        // Ambient chrome: below dialogs (MUI's modal layer), above page content.
        zIndex: Z_INDEX.HEADER_ICONS,
        // The viewport-sized wrapper must never capture clicks; only the tray
        // surface below is interactive.
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        p: 3,
        pr: `${CHAT_FAB_CLEARANCE_PX}px`,
      }}
    >
      <Paper
        component="aside"
        aria-label={LOTTERY_COPY.tray.ariaLabel}
        elevation={8}
        sx={{
          pointerEvents: 'auto',
          width: 360,
          maxWidth: `calc(100vw - ${CHAT_FAB_CLEARANCE_PX + BASE_INSET_PX}px)`,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="subtitle2">
            {LOTTERY_COPY.tray.header}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {metricText !== '' && (
              <Typography
                variant="subtitle2"
                data-testid="tray-metric"
                aria-label={`${metric.loaded} of ${metric.loadable} loaded`}
              >
                {metricText}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={handleToggle}
              aria-expanded={expanded}
              aria-label={
                expanded ? 'Collapse lottery tray' : 'Expand lottery tray'
              }
            >
              {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Box>
        </Box>

        {!isConnected && (
          <Typography
            variant="caption"
            data-testid="tray-connection-paused"
            sx={{ color: 'text.secondary' }}
          >
            {LOTTERY_COPY.tray.updatesPaused}
          </Typography>
        )}

        {expanded ? (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              {batches.map((batch) => (
                <TrayBatchRow key={batch.id} batch={batch} />
              ))}
            </Box>
            <Button
              component={RouterLink}
              to={viewAllPath}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            >
              {LOTTERY_COPY.tray.viewAll}
            </Button>
          </>
        ) : (
          <Typography
            variant="body2"
            data-testid="tray-collapsed-summary"
            sx={{ color: 'text.secondary' }}
          >
            {`${top.current_step_label} · Step ${top.step_number} of ${
              top.step_count || 8
            }`}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default FloatingBatchTray;
